<template>
  <div ref="datePickerDiv" class="ms-date-picker" :class="activeClass" @click="openDatePicker">
    <div class="date-picker-icon">
      <img :src="calendar" class="icon" />
    </div>
    <div class="date-picker-layout date-picker-style" :class="[buttonFontStyle, activeClass]">
      {{ datePicker }}
    </div>
  </div>
  <div style="display: none">
    <flat-pickr
      hidden
      ref="flatpickr"
      v-model="datePicker"
      :config="config"
      placeholder="Select date"
      name="date"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, defineProps, defineEmits } from 'vue'
import flatPickr from 'vue-flatpickr-component'
import { useDateTimeStore } from '@/stores/DateTimeFilterStore'
import { BaseOptions } from 'flatpickr/dist/types/options'
import calendar from '@/assets/svg/calendar.svg'

const dateTimeStore = useDateTimeStore()

const emit = defineEmits(['onOpen'])

const props = defineProps({
  isActive: Boolean
})

const flatpickr = ref<any>(null)
const datePickerDiv = ref<any>(null)

const config = ref<Partial<BaseOptions>>({
  wrap: true,
  altFormat: 'Y-m-d',
  altInput: true,
  dateFormat: 'Y-m-d',
  maxDate: 'today',
  monthSelectorType: 'static',
  position: 'below left',
  locale: {
    firstDayOfWeek: 1 // start week on Monday
  }
})

const datePicker = computed({
  get: () => dateTimeStore.date,
  set: (val) => dateTimeStore.setDate(val)
})

const activeClass = computed(() => `${props.isActive ? 'ms-date-picker-active' : ''}`)
const buttonFontStyle = computed(() => `${props.isActive ? 'NKT-P2-medium' : 'NKT-P2'}`)

const openDatePicker = () => {
  if (!props.isActive) {
    emit('onOpen')
    return
  }

  if (!flatpickr.value.fp.isOpen) {
    flatpickr.value.fp.open(undefined, datePickerDiv.value)
  }
}

watch(
  () => props.isActive,
  (newVal) => {
    if (newVal) {
      flatpickr.value.fp.open(undefined, datePickerDiv.value)
    }
  }
)
</script>

<style scoped lang="scss">
@use '@/styles/ms-color-styles.scss' as color;

.ms-date-picker .date-picker-layout {
  width: 74px;
  display: flex;
  height: 28px;
  padding: 6px var(--border-radius-primary, 0px);
  align-items: center;
}

.ms-date-picker .date-picker-style {
  border: none;
  border-radius: 0%;
  background: color.$color-white;
  &:hover {
    font-weight: 500;
  }
}

.date-picker-icon {
  display: flex;
  padding: 6px var(--border-radius-primary, 0px);
  justify-content: flex-end;
  align-items: center;
  gap: var(--horizontal-gap-medium, 24px);
  align-self: stretch;
}

.ms-date-picker {
  border-radius: var(--border-radius-primary, 0px);
  border: 1.5px solid color.$color-nordic-blue-base;
  background: color.$color-white;

  /* Dropeffect */
  box-shadow: 0px 0px 0.5px 0px rgba(66, 71, 76, 0.32), 0px 4px 8px 0px rgba(66, 71, 76, 0.05);

  display: flex;
  height: 32px;
  padding: var(--border-radius-primary, 0px) 12px;
  align-items: center;
  gap: 12px;
}

.ms-date-picker-active {
  background: color.$color-lighter-grey !important;
  border: 2px solid color.$color-nordic-blue-base;
}
</style>
